import { Component, DestroyRef, ElementRef, OnDestroy, ViewChild, computed, inject, signal } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { DataTableComponent } from '@app/modules/database/components/data-table/data-table.component'
import { LandingPageComponent } from '@app/modules/event-landing-page/components/landing-page/landing-page.component'
import { DCTSetupComponent } from '@app/modules/events/components/dct-setup/dct-setup.component'
import { DtcmSetupComponent } from '@app/modules/events/components/dtcm-setup/dtcm-setup.component'
import { InviteComponent } from '@app/modules/events/components/invite/invite.component'
import { EventPowerUpId } from '@app/v2/event/enums/event-power-up-id.enum'
import { PersonaService } from '@app/v2/personas/services/persona.service'
import { Persona, PersonaClonedPayload, personaResponse } from '@app/v2/personas/types/persona.type'
import { WizardService } from '@app/v2/shared/components/wizard/services/wizard.service'
import { WizardConfig, WizardOpenResult } from '@app/v2/shared/components/wizard/types/wizard.type'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { AppService } from '@app/v2/shared/services/app.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { Response } from '@app/v2/shared/types/generic-response.type'
import { Observable, Subject, catchError, filter, finalize, forkJoin, map, of, skip, switchMap, take, tap, throwError } from 'rxjs'
import { EventStatus } from '../../enums/event.enum'
import { EventWizardComponent } from '../../event-wizard/event-wizard.component'
import { ArchivedItemsService } from '../../services/archived-items.service'
import { EventTimelineSyncService } from '../../services/event-timeline-sync.service'
import { EventService, TimelineRefreshRequest } from '../../services/event.service'
import { EventCardPersonaAction, EventWrapperModel } from '../../types/event-wrapper-model.type'
import { Event, EventFiltersCountData, EventTimelineFilterKey } from '../../types/event.type'
import { InvitationEmailTemplateModel } from '../../types/invitation-email-template.model'
import { parsePublicDateTimeToEpoch } from '../event-dates-and-timings/utils/event-dates-timings.utils'
import { EventWrapperComponent } from '../event-wrapper/event-wrapper.component'
import { InvitationTrackerComponent } from '../invitation-tracker/invitation-tracker.component'
import { SelfServicesComponent } from '../self-services/self-services.component'
import { ZonesComponent } from '../zones/zones.component'
import { ArchivedEventsTimelineComponent } from './components/archived-events-timeline/archived-events-timeline.component'
import { EventAppsComponent } from './components/event-apps/event-apps.component'
import { EventsTimelineEmptyStateComponent } from './components/events-timeline-empty-state/events-timeline-empty-state.component'
import { EventsTimelineHeaderComponent } from './components/events-timeline-header/events-timeline-header.component'
import { EventsTimelineSkeletonCardComponent } from './components/events-timeline-skeleton-card/events-timeline-skeleton-card.component'

type TimelineCriteria = {
  filter: EventTimelineFilterKey
  search: string
}

const DEFAULT_COUNTS: EventFiltersCountData = {
  ALL: 0,
  Live: 0,
  Upcoming: 0,
  Past: 0,
  Archived: 0
}

const EVENT_PERSONAS_PER_PAGE = 5
const DEFAULT_SCROLL_PADDING_TOP = 8
@Component({
  selector: 'app-events-timeline',
  standalone: true,
  imports: [EventsTimelineHeaderComponent, EventsTimelineSkeletonCardComponent, EventsTimelineEmptyStateComponent, EventWrapperComponent],
  providers: [EventService, PersonaService, ArchivedItemsService, EventTimelineSyncService],
  templateUrl: './events-timeline.component.html',
  styleUrl: './events-timeline.component.scss'
})
export class EventsTimelineComponent implements OnDestroy {
  @ViewChild('loadMoreTrigger')
  public set loadMoreTriggerRef(trigger: ElementRef<HTMLDivElement> | undefined) {
    if (!trigger) return
    this._infiniteObserver.disconnect()
    this._infiniteObserver.observe(trigger.nativeElement)
  }
  @ViewChild('eventsTimelineHeader', { read: ElementRef<HTMLElement> }) eventsTimelineHeaderRef!: ElementRef<HTMLElement>

  public readonly events = signal<Event[]>([])
  public readonly filtersCount = signal<EventFiltersCountData>(DEFAULT_COUNTS)
  public readonly selectedFilter = signal<EventTimelineFilterKey>('all')
  public readonly searchTerm = signal('')
  public readonly isInitialLoading = signal(true)
  public readonly isRefreshing = signal(false)
  public readonly isPaginating = signal(false)
  public readonly hasMore = signal(true)
  public readonly currentPage = signal(1)
  public readonly loadingFiltersCount = signal(false)
  public readonly loadingEventCounts = signal(false)
  public readonly loadingInitialPersonas = signal(false)
  public readonly isModalOpen = signal(false)
  public readonly timelineBannerLayout = signal(true)
  public readonly cloningEventId = signal<number | null>(null)
  public readonly cloningMessage = signal('Cloning your event & its configurations...')
  public readonly bannerSaveInProgressEventId = signal<number | null>(null)
  public readonly isCreatingEvent = signal(false)
  public readonly showEmptyState = computed(() => !this.isInitialLoading() && !this.isRefreshing() && this.events().length === 0)
  public readonly canEdit = computed(() => {
    this._userRole()
    return this._workspaceRolesService.canEditEvent()
  })

  public readonly canCreate = computed(() => {
    this._userRole()
    return this._workspaceRolesService.canCreateEvent()
  })

  public readonly canModifyEventOrPersonas = computed(() => {
    this._userRole()
    return this._workspaceRolesService.canModifyEventOrPersonas()
  })

  private readonly _workspaceRolesService = inject(WorkspaceRoleService)
  private readonly _userRole = toSignal(this._workspaceRolesService.getUserRole(), { initialValue: null })
  public readonly scrollPaddingTop = signal<number>(DEFAULT_SCROLL_PADDING_TOP)

  public eventPersonasByEventId: Record<number, Persona[]> = {}
  public eventPersonasTotalByEventId: Record<number, number | null> = {}
  public loadingPersonasByEventId: Record<number, boolean> = {}
  public createPersonaLoadingByEventId: Record<number, boolean> = {}
  public createPersonaApiErrorByEventId: Record<number, unknown | null> = {}

  private readonly _criteriaRequest$ = new Subject<TimelineCriteria>()
  private readonly _perPage = 5
  private readonly _eventService = inject(EventService)
  private readonly _modalService = inject(ModalService)
  private readonly _wizardService = inject(WizardService)
  private readonly _appService = inject(AppService)
  private readonly _authService = inject(AuthService)
  private readonly _alertService = inject(AlertService)
  private readonly _personaService = inject(PersonaService)
  private readonly _archivedItemsService = inject(ArchivedItemsService)
  private readonly _eventTimelineSyncService = inject(EventTimelineSyncService)
  private readonly _router = inject(Router)
  private readonly _route = inject(ActivatedRoute)
  private readonly _destroyRef = inject(DestroyRef)
  private _wizardRefResponse?: WizardOpenResult<unknown>
  private readonly _infiniteObserver = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) return
      this.loadNext()
    },
    { rootMargin: '220px', threshold: 0.01 }
  )

  constructor() {
    const initialState = this._hydrateStateFromQuery()
    this.selectedFilter.set(initialState.filter)
    this.searchTerm.set(initialState.search)

    this._initializeCriteriaFlow()
    this._fetchFiltersCount()
    this._criteriaRequest$.next(initialState)

    this._eventService.timelineRefreshRequested$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((request) => {
      this._handleTimelineRefresh(request)
    })

    this._eventTimelineSyncService.eventPersonasRefreshRequested$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((request) => {
      this._handleEventPersonasRefresh(request)
    })

    const pendingRefresh = this._eventService.pendingTimelineRefresh
    if (pendingRefresh) {
      this._eventService.pendingTimelineRefresh = null
      this._handleTimelineRefresh(pendingRefresh)
    }

    const pendingPersonasRefresh = this._eventTimelineSyncService.pendingEventPersonasRefresh
    if (pendingPersonasRefresh) {
      this._eventTimelineSyncService.pendingEventPersonasRefresh = null
      this._handleEventPersonasRefresh(pendingPersonasRefresh)
    }
  }

  ngOnInit(): void {
    this._authService.currentWorkspaceChange
      .pipe(
        skip(1),
        filter((workspace) => !!workspace),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        const initialState = this._hydrateStateFromQuery()
        this.selectedFilter.set(initialState.filter)
        this.searchTerm.set(initialState.search)

        this._fetchFiltersCount()
        this._criteriaRequest$.next(initialState)
        this._wizardService.closeAllWizardLayers()
      })
  }

  /**
   * Creates a persona for an event and appends it to that event persona list.
   * @param payload {{ event: Event; name: string }} - The event + persona name.
   */
  public onCreatePersonaSubmitted(payload: { event: Event; name: string }): void {
    const name = payload.name.trim()
    if (!name) return

    const event = payload.event
    if (this.createPersonaLoadingByEventId[event.id]) return

    this.createPersonaApiErrorByEventId = { ...this.createPersonaApiErrorByEventId, [event.id]: null }
    this.createPersonaLoadingByEventId = { ...this.createPersonaLoadingByEventId, [event.id]: true }

    this._personaService
      .createPersona(event.workspace_id, event.id, name)
      .pipe(
        finalize(() => {
          this.createPersonaLoadingByEventId = { ...this.createPersonaLoadingByEventId, [event.id]: false }
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => {
          const data = res.data
          const created = data == null ? undefined : Array.isArray(data) ? data[0] : data
          this._alertService.alert(Alert.SUCCESS, res.message)
          this.createPersonaApiErrorByEventId = { ...this.createPersonaApiErrorByEventId, [event.id]: null }
          if (!created) {
            this._alertService.alert(Alert.ERROR, 'Unexpected response when creating persona.')
            return
          }

          const current = this.eventPersonasByEventId[event.id] ?? []
          this.eventPersonasByEventId = {
            ...this.eventPersonasByEventId,
            [event.id]: [...current.filter((persona) => persona.id !== created.id), created]
          }
          const prevTotal = this.eventPersonasTotalByEventId[event.id]
          if (prevTotal != null) {
            this.eventPersonasTotalByEventId = {
              ...this.eventPersonasTotalByEventId,
              [event.id]: prevTotal + 1
            }
          }
        },
        error: (err) => {
          const body = err?.error ?? null
          const fieldErrors = this._extractFieldErrors(body, 'name')
          if (fieldErrors.length) {
            this._alertService.alert(Alert.ERROR, fieldErrors[0])
            this.createPersonaApiErrorByEventId = { ...this.createPersonaApiErrorByEventId, [event.id]: null }
            return
          }

          this.createPersonaApiErrorByEventId = { ...this.createPersonaApiErrorByEventId, [event.id]: null }
          this._alertService.alert(Alert.ERROR, this._extractErrorMessage(body, 'Could not create persona. Please try again.'))
        }
      })
  }

  /**
   * Updates an existing persona name for an event and keeps local map in sync.
   * @param payload {{ event: Event; persona: Persona }} - Event and edited persona data.
   */
  public onPersonaNameUpdate(payload: { event: Event; persona: Persona }): void {
    const event = payload.event
    const persona = payload.persona
    const nextName = String(persona.name ?? '').trim()
    if (!nextName) return

    const currentList = this.eventPersonasByEventId[event.id] ?? []
    const currentPersona = currentList.find((item) => item.id === persona.id)
    const currentName = String(currentPersona?.name ?? '').trim()
    if (nextName === currentName) return

    this._personaService
      .updatePersonaName(event.workspace_id, event.id, persona.id, nextName)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res) => {
          const updated = res.data
          this._updateEventPersonas(event.id, (list) => list.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)))
          this._alertService.alert(Alert.SUCCESS, res.message)
        },
        error: (err) => {
          const body = err?.error ?? null
          const fieldNameErrors = this._extractFieldErrors(body, 'name')

          if (fieldNameErrors.length) {
            // Re-emit the saved value into child controls so mobile inline input reverts like personas wizard.
            this._updateEventPersonas(event.id, (list) => [...list])
            this._alertService.alert(Alert.ERROR, fieldNameErrors[0])
            return
          }

          this._alertService.alert(Alert.ERROR, this._extractErrorMessage(body, 'Could not update persona name. Please try again.'))
        }
      })
  }

  /**
   * Opens landing page setup in the overlay wizard (same shell as timeline new-event-card).
   * @param payload {{ event: Event; persona: Persona }} - Event + selected persona.
   */
  /**
   * Handle a power-up card click from any event row in the timeline.
   * Dispatches per power-up id as defined on each card's data object.
   * @param payload {{ event: Event; powerUp: EventPowerUpId }} - Event and clicked power-up id.
   */
  public onPowerUpClicked(payload: { event: Event; powerUp: EventPowerUpId }): void {
    switch (payload.powerUp) {
      case EventPowerUpId.Crew:
        this._openAssignCrew(payload.event)
        break
      case EventPowerUpId.Zones:
        this._openZones(payload.event)
        break
      case EventPowerUpId.SelfPrinting:
        this._openSelfPrinting(payload.event)
        break
      case EventPowerUpId.Dct:
        this._openDCT(payload.event)
        break
      case EventPowerUpId.Det:
        this._openDET(payload.event)
        break
      case EventPowerUpId.EventApps:
        this._openEventApps(payload.event)
        break
      case EventPowerUpId.InvitationTracker:
        this._openInvitationTracker(payload.event)
        break
      default:
        break
    }
  }

  /**
   * Open the Assign Crew wizard for an event.
   * @param event {Event} - The event to assign crew for.
   */
  private _openAssignCrew(event: Event): void {
    this._wizardRefResponse = this._wizardService.open({
      component: InviteComponent,
      componentInputs: {
        eventId: event.id
      },
      headerConfig: {
        breadcrumbs: [event.name, 'Assign Crew']
      }
    })
  }

  /**
   * Opens zones wizard for an event.
   * @param event {Event} - The event to open zones for.
   */
  private _openZones(event: Event): void {
    this._wizardService.open({
      component: ZonesComponent,
      componentInputs: {
        eventId: event.id
      },
      headerConfig: { breadcrumbs: [event.name, 'Zone Management'] }
    })
  }

  /**
   * Opens self printing wizard for an event.
   * @param event {Event} - The event to open self printing for.
   */
  private _openSelfPrinting(event: Event): void {
    this._wizardService.open({
      component: SelfServicesComponent,
      componentInputs: { organizationSlug: this._authService.currentWorkspace.slug, eventId: event.id, eventSlug: event.slug },
      headerConfig: { breadcrumbs: [event.name, 'Self Printing'] }
    })
  }

  /**
   * Opens DCT setup for an event.
   * @param event {Event} - The event to open DCT setup for.
   */
  private _openDCT(event: Event): void {
    this._wizardRefResponse = this._wizardService.open({
      component: DCTSetupComponent,
      componentInputs: {
        eventId: event.id
      },
      headerConfig: { breadcrumbs: [event.name, 'DCT connection'] }
    })
  }

  /**
   * Opens DET setup for an event.
   * @param event {Event} - The event to open DET setup for.
   */
  private _openDET(event: Event): void {
    this._wizardRefResponse = this._wizardService.open({
      component: DtcmSetupComponent,
      componentInputs: { eventId: event.id },
      headerConfig: { breadcrumbs: [event.name, 'DET connection'] }
    })
  }

  /**
   * Opens event apps wizard for an event.
   * @param event {Event} - The event to open event apps for.
   */
  private _openEventApps(event: Event): void {
    this._wizardService.open({
      component: EventAppsComponent,
      componentInputs: { eventId: event.id, eventName: event.name, eventSlug: event.slug },
      headerConfig: { breadcrumbs: [event.name, 'Event Apps'] }
    })
  }

  /**
   * Opens invitation tracker wizard for an event.
   * Day timings for the email-template preview are loaded inside the tracker.
   * @param event {Event} - The event to open invitation tracker for.
   */
  private _openInvitationTracker(event: Event): void {
    this._wizardService.open({
      component: InvitationTrackerComponent,
      componentInputs: {
        eventId: event.id,
        eventName: event.name,
        eventSlug: event.slug,
        isEventFinished: this._isEventFinished(event),
        emailTemplateEvent: {
          name: event.name,
          slug: event.slug ?? undefined,
          public_start_date: event.public_start_date,
          public_end_date: event.public_end_date,
          location_details: event.location_details ?? null
        },
        emailTemplateTimings: InvitationEmailTemplateModel.mapEventTimings(event.timings)
      },
      headerConfig: { breadcrumbs: [event.name, 'Invitation Tracking'] }
    })
  }

  /**
   * Checks whether an event has ended and should be read-only for invitation imports.
   * List filter uses EventStatus.FINISHED ("Past"); event payloads may use "FINISHED".
   * @param event {Event} - The event to check.
   */
  private _isEventFinished(event: Event): boolean {
    const status = event.status?.trim().toLowerCase()
    if (status === EventStatus.FINISHED.toLowerCase() || status === 'finished') return true

    const endDate = event.public_end_date?.trim()
    if (!endDate || event.is_indefinitely) return false

    return parsePublicDateTimeToEpoch(endDate, event.timezone_offset_string) < Date.now()
  }

  public onPersonaLandingPageClick(payload: { event: Event; persona: Persona }): void {
    if (!payload.persona?.id) return

    this._wizardRefResponse = this._wizardService.open({
      component: LandingPageComponent,
      componentInputs: {
        event_id: payload.event.id,
        persona_id: payload.persona.id
      },
      headerConfig: {
        breadcrumbs: [payload.event.name, 'Landing Page']
      }
    })
  }

  /**
   * Opens database view and preselects the clicked persona.
   * @param payload {{ event: Event; persona: Persona }} - Event and selected persona.
   */
  public onPersonaViewRegistrantsClick(payload: { event: Event; persona: Persona }): void {
    this._wizardService.open({
      component: DataTableComponent,
      componentInputs: {
        eventId: payload.event.id,
        selectedPersonaSlug: payload.persona.slug
      },
      headerConfig: { breadcrumbs: [payload.event.name, 'Database'] }
    })
  }

  /**
   * Appends a successfully cloned persona to the event personas map.
   * @param payload {PersonaClonedPayload & { event: Event }} - Event, cloned persona, and API message.
   */
  public onPersonaCloned(payload: PersonaClonedPayload & { event: Event }): void {
    const { event, persona: clonedPersona, message } = payload
    this._updateEventPersonas(event.id, (list) => [...list.filter((item) => item.id !== clonedPersona.id), clonedPersona])
    this._alertService.alert(Alert.SUCCESS, message)
    const prevTotal = this.eventPersonasTotalByEventId[event.id]
    if (prevTotal != null) {
      this.eventPersonasTotalByEventId = { ...this.eventPersonasTotalByEventId, [event.id]: prevTotal + 1 }
    }
  }

  /**
   * Removes persona from event and updates local list.
   * @param payload {{ event: Event; persona: Persona }} - Event and selected persona.
   */
  public onPersonaArchiveClick(payload: { event: Event; persona: Persona; personaIdsToArchive: number[] }): void {
    const { event, persona, personaIdsToArchive } = payload
    this._optimisticallyRemovePersonas(event, personaIdsToArchive)
    this.loadingPersonasByEventId = { ...this.loadingPersonasByEventId, [event.id]: true }

    this._archivedItemsService
      .archiveItems(event.workspace_id, event.id, persona.id)
      .pipe(
        switchMap((res) => this._reloadEventPersonasPage$(event).pipe(map(() => res))),
        catchError((err) => this._reloadEventPersonasPage$(event).pipe(switchMap(() => throwError(() => err)))),
        finalize(() => {
          this.loadingPersonasByEventId = { ...this.loadingPersonasByEventId, [event.id]: false }
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => {
          this._alertService.alert(Alert.SUCCESS, res.message)
        },
        error: (err) => {
          this._alertService.alert(Alert.ERROR, this._extractErrorMessage(err?.error || err.message, 'Could not archive persona. Please try again.'))
        }
      })
  }

  /**
   * Handle persona actions
   * @param action {EventCardPersonaAction} - The action
   */
  public onPersonaAction(action: EventCardPersonaAction): void {
    switch (action.type) {
      case 'createPersonaSubmitted':
        this.onCreatePersonaSubmitted(action.payload)
        return
      case 'personaNameUpdate':
        this.onPersonaNameUpdate(action.payload)
        return
      case 'personaLandingPageClick':
        this.onPersonaLandingPageClick(action.payload)
        return
      case 'personaViewRegistrantsClick':
        this.onPersonaViewRegistrantsClick(action.payload)
        return
      case 'personaCloned':
        this.onPersonaCloned(action.payload)
        return
      case 'personaArchiveClick':
        this.onPersonaArchiveClick(action.payload)
        return
      case 'legacyPersonaEditorClosed':
        this._refreshTimelineEventInList(action.payload.event.id)
        return
      case 'personasListingWizardClosed':
        this.refreshEventPersonasFromServer(action.payload.event)
        return
      default:
        return
    }
  }

  /**
   * Updates the event personas by event id
   * @param eventId {number} - The event id
   * @param updater {function} - The updater function
   */
  private _updateEventPersonas(eventId: number, updater: (list: Persona[]) => Persona[]): void {
    const current = this.eventPersonasByEventId[eventId] ?? []
    this.eventPersonasByEventId = {
      ...this.eventPersonasByEventId,
      [eventId]: updater(current)
    }
  }

  /**
   * Optimistically removes personas from the event personas list.
   * @param event {Event} - The event
   * @param personaIdsToArchive {number[]} - The persona ids to remove
   */
  private _optimisticallyRemovePersonas(event: Event, personaIdsToArchive: number[]): void {
    if (!personaIdsToArchive.length) return

    const removeSet = new Set(personaIdsToArchive)
    this._updateEventPersonas(event.id, (list) => list.filter((persona) => !removeSet.has(persona.id)))

    const prevTotal = this.eventPersonasTotalByEventId[event.id]
    if (prevTotal != null) {
      this.eventPersonasTotalByEventId = {
        ...this.eventPersonasTotalByEventId,
        [event.id]: Math.max(0, prevTotal - personaIdsToArchive.length)
      }
    }
  }

  /**
   * Extracts the field errors from the body
   * @param body {unknown} - The body
   * @param field {string} - The field
   * @returns {string[]} - The field errors
   */
  private _extractFieldErrors(body: unknown, field: string): string[] {
    if (!body || typeof body !== 'object' || Array.isArray(body)) return []
    const candidate = (body as Record<string, unknown>)[field]
    if (!Array.isArray(candidate)) return []
    return candidate.filter((message): message is string => typeof message === 'string')
  }

  /**
   * Extracts the error message from the body
   * @param body {unknown} - The body
   * @param fallback {string} - The fallback message
   * @returns {string} - The error message
   */
  private _extractErrorMessage(body: unknown, fallback: string): string {
    if (typeof body === 'object' && body !== null && 'message' in body && typeof (body as { message: unknown }).message === 'string') {
      return (body as { message: string }).message
    }
    return fallback
  }
  /**
   * Handle filter/search changes emitted by timeline header.
   */
  public onCriteriaChanged(criteria: TimelineCriteria): void {
    this._criteriaRequest$.next({
      filter: criteria.filter,
      search: criteria.search.trim()
    })
  }

  /**
   * Resolve empty-state mode by active filter/search.
   * Limited-access members with an empty list (no search / all filter) see the archived-assigned copy.
   */
  public emptyStateMode(): 'all' | 'filtered' | 'search' | 'all-archived' {
    this._userRole()
    if (this.searchTerm().trim()) return 'search'
    if (this.selectedFilter() !== 'all') return 'filtered'
    if (this._workspaceRolesService.isLimitedAccess()) return 'all-archived'
    return 'all'
  }

  /**
   * Resolve empty-state illustration source.
   */
  public emptyStateImageSrc(): string {
    const mode = this.emptyStateMode()
    if (mode === 'all-archived') return 'assets/images/v2/all-allowed-events-archived.svg'
    return mode === 'all' ? 'assets/images/v2/noEvents.svg' : 'assets/images/v2/noEvents2.svg'
  }

  /**
   * Resolve current selected filter label for empty-state text.
   */
  public selectedFilterLabel(): string {
    if (this.selectedFilter() === 'live') return 'live'
    if (this.selectedFilter() === 'upcoming') return 'upcoming'
    if (this.selectedFilter() === 'finished') return 'past'
    return 'events'
  }

  /**
   * Open archived events list inside wizard and keep current selected chip.
   */
  public openArchivedEventsWizard(): void {
    this._wizardService.open({
      component: ArchivedEventsTimelineComponent,
      componentInputs: {
        archivedCount: this.filtersCount().Archived ?? 0
      },
      headerConfig: {
        breadcrumbs: ['Archived events']
      }
    })
  }

  /**
   * Keep minimal modal state support for card outputs.
   */
  public setModalOpen(isOpen: boolean): void {
    this.isModalOpen.set(isOpen)
  }

  /**
   * Compatibility hook for wrapper output.
   */
  public setArchiveUpdate(_isArchiveUpdate: boolean): void {}

  /**
   * Check if the event is being cloned
   * @param event {Event} - The event
   * @returns {boolean} - Whether the event is being cloned
   */
  public isHeaderCloningFor(event: Event): boolean {
    return this.cloningEventId() !== null && this.cloningEventId() === event.id
  }

  /**
   * Get the event wrapper model
   * @param event {Event} - The event
   * @param isLast {boolean} - Whether the event is the last one
   * @returns {EventWrapperModel} - The event wrapper model
   */
  public getEventWrapperModel(event: Event, isLast: boolean): EventWrapperModel {
    return this._eventService.buildActiveTimelineWrapperModel({
      event,
      isLast,
      events: this.events(),
      canEdit: this.canEdit(),
      canCreate: this.canCreate(),
      headerCloning: this.isHeaderCloningFor(event),
      headerCloningMessage: this.cloningMessage(),
      timelineBannerLayout: this.timelineBannerLayout(),
      loadingCounts: this.loadingEventCounts(),
      bannerSaveInProgress: this.bannerSaveInProgressEventId() === event.id,
      personas: this.eventPersonasByEventId[event.id] ?? [],
      personasTotal: this.eventPersonasTotalByEventId[event.id] ?? null,
      personasLoading: this.loadingPersonasByEventId[event.id] ?? false,
      createPersonaPending: this.createPersonaLoadingByEventId[event.id] ?? false,
      createPersonaApiError: this.createPersonaApiErrorByEventId[event.id] ?? null,
      canModifyEventOrPersonas: this.canModifyEventOrPersonas()
    })
  }

  /**
   * Clone flow from event header
   * @param event {Event} - The event
   */
  public onHeaderCloneConfirmed(event: Event): void {
    if (!this._workspaceRolesService.canCloneEvent()) return
    this.cloningMessage.set('Cloning your event & its configurations...')
    this.cloningEventId.set(event.id)
    this._eventService
      .cloneEvent(event.workspace_id, event.id)
      .pipe(
        finalize(() => {
          this.cloningEventId.set(null)
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response: Response<Event>) => {
          this._closeCloneModalIfOpen(event.id)
          this.setModalOpen(false)
          this._alertService.alert(Alert.SUCCESS, response.message)
          this._eventService.requestTimelineRefresh({ type: 'list' })
          this._openEventWizard(
            {
              component: EventWizardComponent,
              componentInputs: {
                eventId: response.data.id,
                industrySectors: this._appService.industrySectors
              },
              headerConfig: {
                breadcrumbs: [response.data.name]
              }
            },
            { type: 'event', eventId: response.data.id }
          )
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error.error.message)
          this._closeCloneModalIfOpen(event.id)
          this.setModalOpen(false)
        }
      })
  }

  /**
   * Archive flow from event header
   * @param event {Event} - The event
   */
  public onHeaderArchiveConfirmed(event: Event): void {
    if (!this._workspaceRolesService.canArchiveEvent()) return
    this._eventService
      .updateEventData(event.workspace_id, event.id, { is_archived: true } as Partial<Event>)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._alertService.alert(Alert.SUCCESS, 'Your event has been successfully archived')
          this._closeArchiveModalIfOpen(event.id)
          this.setModalOpen(false)
          this.events.set(this.events().filter((currentEvent) => currentEvent.id !== event.id))
          this._fetchFiltersCount()
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error.error.message)
          this._closeArchiveModalIfOpen(event.id)
          this.setModalOpen(false)
        }
      })
  }

  /**
   * Restore flow from event card in archived mode.
   * @param event {Event} - The event
   */
  public onHeaderRestoreConfirmed(event: Event): void {
    if (!this._workspaceRolesService.canRestoreEvent()) return
    this._eventService
      .updateEventData(event.workspace_id, event.id, { is_archived: false } as Partial<Event>)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response: Response<Event>) => {
          this._alertService.alert(Alert.SUCCESS, response.message)
          this._closeRestoreModalIfOpen(event.id)
          this.setModalOpen(false)
          this.events.set(this.events().filter((currentEvent) => currentEvent.id !== event.id))
          this._fetchFiltersCount()
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error.error.message)
          this._closeRestoreModalIfOpen(event.id)
          this.setModalOpen(false)
        }
      })
  }

  /**
   * Close the clone modal if it is open
   * @param eventId {number} - The event id
   */
  private _closeCloneModalIfOpen(eventId: number): void {
    const modalId = `clone-event-modal-${eventId}`
    if (this._modalService.isModalOpen(modalId)) {
      this._modalService.toggleModal(modalId)
    }
  }

  /**
   * Close the archive modal if it is open
   * @param eventId {number} - The event id
   */
  private _closeArchiveModalIfOpen(eventId: number): void {
    const modalId = `archive-event-modal-${eventId}`
    if (this._modalService.isModalOpen(modalId)) {
      this._modalService.toggleModal(modalId)
    }
  }

  /**
   * Close the restore confirmation modal if it is open
   * @param eventId {number} - The event id
   */
  private _closeRestoreModalIfOpen(eventId: number): void {
    const modalId = `restore-event-modal-${eventId}`
    if (this._modalService.isModalOpen(modalId)) {
      this._modalService.toggleModal(modalId)
    }
  }

  /**
   * Load the next events
   */
  public loadNext(): void {
    if (this.isPaginating() || this.isInitialLoading() || this.isRefreshing() || this.loadingEventCounts() || this.isModalOpen() || !this.hasMore()) return
    const nextPage = this.currentPage() + 1
    this.currentPage.set(nextPage)
    this.isPaginating.set(true)
    this._loadEventsPage(
      nextPage,
      {
        filter: this.selectedFilter(),
        search: this.searchTerm()
      },
      false
    )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe()
  }

  /**
   * Refresh personas for event when personas wizard closes.
   */
  public refreshEventPersonasFromServer(event: Event, options?: { silent?: boolean }): void {
    const silent = options?.silent === true
    if (!silent) {
      this.loadingPersonasByEventId = { ...this.loadingPersonasByEventId, [event.id]: true }
    }
    this._reloadEventPersonasPage$(event)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => {
          if (!silent) {
            this.loadingPersonasByEventId = { ...this.loadingPersonasByEventId, [event.id]: false }
          }
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe()
  }

  /**
   * Reloads the event personas page.
   * @param event {Event} - The event to reload the personas for.
   * @returns {Observable<Persona[]>} - An observable of the personas.
   */
  private _reloadEventPersonasPage$(event: Event): Observable<personaResponse> {
    return this._personaService.getPersonas(event.workspace_id, event.id, EVENT_PERSONAS_PER_PAGE, 1).pipe(
      catchError(() => of<personaResponse>({ data: [], message: '', total: 0 })),
      tap((response) => {
        this.eventPersonasByEventId = {
          ...this.eventPersonasByEventId,
          [event.id]: response.data ?? []
        }
        this.eventPersonasTotalByEventId = {
          ...this.eventPersonasTotalByEventId,
          [event.id]: response.total ?? null
        }
      })
    )
  }

  /**
   * Persist banner changes from the card (upload / delete / crop path), same as event-wizard `updateEventData` for `cover_page`.
   * @param change { { field: string; value: string } } - The banner change
   */
  public onBannerChanged(change: { field: string; value: string; original_image?: string; event: Event }): void {
    const { event, field, value } = change
    const incoming = String(value ?? '').trim()
    const current = String(event.cover_page ?? '').trim()
    if (incoming === current) return

    const previousEvents = this.events()
    // Optimistically update local list so banner UI refreshes immediately,
    // even if backend response omits/doesn't echo the updated banner path.
    this.events.set(this.events().map((item) => (item.id === event.id ? { ...item, [field]: value } : item)))

    this.bannerSaveInProgressEventId.set(event.id)
    this._eventService
      .updateEventData(event.workspace_id, event.id, { [field]: value } as Partial<Event>)
      .pipe(
        finalize(() => this.bannerSaveInProgressEventId.set(null)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response: Response<Event>) => {
          this._alertService.alert(Alert.SUCCESS, response.message)
          const idx = this.events().findIndex((e) => e.id === event.id)
          if (idx >= 0) {
            const next = [...this.events()]
            next[idx] = { ...next[idx], ...response.data }
            this.events.set(next)
          }
        },
        error: (error: { error?: { message?: string }; message?: string }) => {
          const message = error?.error?.message || error?.message || 'Failed to update event'
          this._alertService.alert(Alert.ERROR, message)
          this.events.set(previousEvents)
        }
      })
  }

  /**
   * Create a new event
   */
  public createEvent(): void {
    if (!this.canCreate() || this.isCreatingEvent()) return
    this.isCreatingEvent.set(true)
    this._eventService
      .createEvent(this._authService.currentWorkspace.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response: Response<Event>) => {
          this.isCreatingEvent.set(false)
          this._openEventWizard(
            {
              component: EventWizardComponent,
              componentInputs: {
                eventId: response.data.id,
                industrySectors: this._appService.industrySectors
              },
              headerConfig: {
                breadcrumbs: ['Event']
              }
            },
            { type: 'list' }
          )
        },
        error: (error) => {
          this.isCreatingEvent.set(false)
          this._alertService.alert(Alert.ERROR, error.message || 'Failed to create event')
        }
      })
  }
  /**
   * Destroy the component
   */
  public ngOnDestroy(): void {
    this._infiniteObserver.disconnect()
    this._wizardRefResponse?.close()
  }

  /**
   * Opens the event wizard overlay and refreshes the timeline when it closes.
   */
  private _openEventWizard(config: WizardConfig<unknown>, refresh: TimelineRefreshRequest): void {
    const result = this._wizardService.open(config)
    this._wizardRefResponse = result
    result.onClose.pipe(take(1), takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this._eventService.requestTimelineRefresh(refresh)
    })
  }

  private _handleTimelineRefresh(request: TimelineRefreshRequest): void {
    if (request.type === 'list') {
      this._refreshTimelineFromStart()
      return
    }
    this._refreshTimelineEventInList(request.eventId)
  }

  /**
   * Silently reload personas for a visible event (e.g. after restoring from the archive wizard).
   */
  private _handleEventPersonasRefresh(eventId: number): void {
    const event = this.events().find((item) => item.id === eventId)
    if (!event) return

    this.refreshEventPersonasFromServer(event, { silent: true })
  }

  private _refreshTimelineFromStart(): void {
    this._fetchFiltersCount()
    this._criteriaRequest$.next({
      filter: this.selectedFilter(),
      search: this.searchTerm()
    })
  }

  private _refreshTimelineEventInList(eventId: number): void {
    const workspaceId = this._authService.currentWorkspace?.id
    if (!workspaceId) return

    const index = this.events().findIndex((event) => event.id === eventId)
    if (index === -1) return

    this._eventService
      .getEventListItem(workspaceId, eventId)
      .pipe(
        switchMap((response) => this._decorateEvents([response.data]).pipe(map(([event]) => event))),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (updatedEvent) => {
          const next = [...this.events()]
          next[index] = updatedEvent
          this.events.set(next)
          this.refreshEventPersonasFromServer(updatedEvent, { silent: true })
          this._fetchFiltersCount()
        }
      })
  }

  /**
   * Initialize switchMap flow for filter/search requests.
   */
  private _initializeCriteriaFlow(): void {
    this._criteriaRequest$
      .pipe(
        map((criteria) => ({
          filter: criteria.filter,
          search: criteria.search.trim()
        })),
        switchMap((criteria) => {
          this.selectedFilter.set(criteria.filter)
          this.searchTerm.set(criteria.search)
          this.currentPage.set(1)
          this.hasMore.set(true)

          if (this.isInitialLoading()) {
            this.isRefreshing.set(false)
          } else {
            this.isRefreshing.set(true)
          }

          this._syncQueryParams(criteria)
          return this._loadEventsPage(1, criteria, true)
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe()
  }

  /**
   * Fetch paginated events and merge into list state.
   */
  private _loadEventsPage(page: number, criteria: TimelineCriteria, resetList: boolean) {
    return this._eventService
      .getEventsList({
        organizationId: this._authService.currentWorkspace.id,
        page,
        perPage: this._perPage,
        search: criteria.search,
        status: this._statusFromFilter(criteria.filter)
      })
      .pipe(
        switchMap((response) => {
          const currentEvents = resetList ? [] : this.events()
          const merged = resetList ? response.data : [...currentEvents, ...response.data]
          this.events.set(merged)
          this.hasMore.set(response.current_page < response.last_page)
          this.isInitialLoading.set(false)
          this.isRefreshing.set(false)

          const pageEventIds = new Set(response.data.map((event) => event.id))
          const decorate$ = this._decorateEvents(response.data).pipe(
            tap((decoratedEvents) => {
              const decoratedById = new Map(decoratedEvents.map((event) => [event.id, event]))
              this.events.set(this.events().map((event) => (pageEventIds.has(event.id) ? decoratedById.get(event.id) ?? event : event)))
            }),
            catchError(() => of(response.data)),
            map(() => void 0)
          )

          return forkJoin([decorate$, this._loadEventPersonas(merged, resetList)]).pipe(map(() => void 0))
        }),
        finalize(() => {
          this.isPaginating.set(false)
        })
      )
  }

  /**
   * Enrich event list with registration counts.
   */
  private _decorateEvents(events: Event[]) {
    if (!events.length) return of(events)

    this.loadingEventCounts.set(true)
    return this._eventService.getEventCounts(events).pipe(finalize(() => this.loadingEventCounts.set(false)))
  }

  /**
   * Fetch timeline header counts.
   */
  private _fetchFiltersCount(): void {
    if (!this._authService.currentWorkspace?.id)
      return this.filtersCount.set({
        ALL: 0,
        Live: 0,
        Upcoming: 0,
        Past: 0,
        Archived: 0
      })
    this.loadingFiltersCount.set(true)
    this._eventService
      .getEventsFiltersCount(this._authService.currentWorkspace.id)
      .pipe(
        finalize(() => this.loadingFiltersCount.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response) => {
          this.filtersCount.set({
            ALL: response.data?.ALL ?? 0,
            Live: response.data?.Live ?? 0,
            Upcoming: response.data?.Upcoming ?? 0,
            Past: response.data?.Past ?? 0,
            Archived: response.data?.Archived ?? 0
          })
        },
        error: () => {
          this.filtersCount.set(DEFAULT_COUNTS)
        }
      })
  }

  /**
   * Map current filter/search state to route query params.
   */
  private _syncQueryParams(criteria: TimelineCriteria): void {
    const status = criteria.filter === 'all' ? null : criteria.filter
    const search = criteria.search ? criteria.search : null
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: {
        status,
        search
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    })
  }

  /**
   * Hydrate timeline state from query params on first render.
   */
  private _hydrateStateFromQuery(): TimelineCriteria {
    const queryStatus = this._route.snapshot.queryParamMap.get('status')
    const search = (this._route.snapshot.queryParamMap.get('search') ?? '').trim()
    return {
      filter: this._queryStatusToFilter(queryStatus),
      search
    }
  }

  /**
   * Convert query status string to internal filter key.
   */
  private _queryStatusToFilter(queryStatus: string | null): EventTimelineFilterKey {
    if (queryStatus === 'live') return 'live'
    if (queryStatus === 'upcoming') return 'upcoming'
    if (queryStatus === 'finished') return 'finished'
    return 'all'
  }

  /**
   * Convert filter key to API status value.
   */
  private _statusFromFilter(filter: EventTimelineFilterKey): string | undefined {
    if (filter === 'live') return EventStatus.LIVE
    if (filter === 'upcoming') return EventStatus.UPCOMING
    if (filter === 'finished') return EventStatus.FINISHED
    return undefined
  }

  /**
   * Fetch personas for visible events list.
   */
  private _loadEventPersonas(events: Event[], reset: boolean) {
    if (reset) {
      this.loadingInitialPersonas.set(false)
      this.eventPersonasByEventId = {}
      this.eventPersonasTotalByEventId = {}
      this.loadingPersonasByEventId = {}
      this.createPersonaLoadingByEventId = {}
      this.createPersonaApiErrorByEventId = {}
    }

    if (!events.length) {
      this.loadingInitialPersonas.set(false)
      return of(void 0)
    }

    const eventIds = new Set(events.map((event) => event.id))
    const currentLoading: Record<number, boolean> = {}
    events.forEach((event) => {
      currentLoading[event.id] = this.loadingPersonasByEventId[event.id] ?? false
    })
    this.loadingPersonasByEventId = currentLoading

    const eventsToFetch = events.filter((event) => !Object.prototype.hasOwnProperty.call(this.eventPersonasByEventId, event.id))
    if (!eventsToFetch.length) {
      this.loadingInitialPersonas.set(false)
      return of(void 0)
    }

    if (reset) {
      this.loadingInitialPersonas.set(true)
    }

    const requests = eventsToFetch.map((event) => {
      this.loadingPersonasByEventId = { ...this.loadingPersonasByEventId, [event.id]: true }
      return this._personaService.getPersonas(event.workspace_id, event.id, EVENT_PERSONAS_PER_PAGE, 1).pipe(
        map((response) => ({ eventId: event.id, personas: response.data ?? [], total: response.total ?? null })),
        catchError(() => of({ eventId: event.id, personas: [] as Persona[], total: 0 }))
      )
    })

    return forkJoin(requests).pipe(
      map((results) => {
        const personasByEventId = { ...this.eventPersonasByEventId }
        const totalsByEventId = { ...this.eventPersonasTotalByEventId }
        const loadingByEventId = { ...this.loadingPersonasByEventId }

        results.forEach((result) => {
          if (!eventIds.has(result.eventId)) return
          personasByEventId[result.eventId] = result.personas
          totalsByEventId[result.eventId] = result.total
          loadingByEventId[result.eventId] = false
        })

        this.eventPersonasByEventId = personasByEventId
        this.eventPersonasTotalByEventId = totalsByEventId
        this.loadingPersonasByEventId = loadingByEventId
        this.loadingInitialPersonas.set(false)
        return void 0
      })
    )
  }

  /**
   * Handle the display of the floating header.
   * @param isDisplaying - Whether the header is displaying.
   */
  public onDisplayFloatingHeader(isDisplaying: boolean): void {
    this.scrollPaddingTop.set(isDisplaying ? DEFAULT_SCROLL_PADDING_TOP + this.eventsTimelineHeaderRef?.nativeElement?.clientHeight : DEFAULT_SCROLL_PADDING_TOP)
  }
}
